import { callExternalApi, CallResult } from '../callExternalApi';
import { AddressInfo } from '../../types';
import type { Tag } from '../../store/Tags';

export type WorkflowTab =
  | 'submitted'
  | 'quoting_in_progress'
  | 'reviewing_approving_quotes'
  | 'upstack_processing_order'
  | 'hand_off_to_pm';

export type WorkflowSourceType = 'opportunity' | 'project';

export type WorkflowNewLocation = {
  address: string;
  locationCode?: string;
  locationName?: string;
  floorSuiteRoom?: string;
};

export type WorkflowOrder = {
  // `id` is the Salesforce id; tag relations key on the portal id, which rides along separately.
  id: string;
  sf_id: string;
  upstack_id: string;
  // The Opportunity the order began as — on an opportunity row that is the row itself.
  originating_opportunity_sf_id?: string | null;
  source_type: WorkflowSourceType;
  workflow_tab: WorkflowTab;
  sf_stage: string | null;
  request_status: string | null;
  name: string | null;
  request_number?: string | null;
  account_id: string | null;
  account_name: string | null;
  created_at: string | null;
  stage_entered_at: string | null;
  requested_by: string | null;
  request_type: string | null;
  service_type: string | null;
  locations: string[];
  new_locations: WorkflowNewLocation[];
  preferred_term: string | null;
  location_code?: string | null;
  location_name?: string | null;
  details?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  advanced_fields: Record<string, any>;
  supplier_name?: string | null;
  addressInfo?: AddressInfo;
  tags?: Tag[];
};

export type Quote = {
  id: string;
  quote_id: string;
  source_type: 'quote';
  service: string | null;
  supplier: string | null;
  term: number;
  mrc: string | null;
  nrc: string | null;
  notes: string;
  approved: boolean;
  tags?: Tag[];
};

export type QuoteApprovalResponse = {
  id: string;
  opportunity_id: string;
  account_id: string;
  portal_user_email: string;
  quote_ids: string[];
  approved_at: string;
};

export const getWorkflowOrders = async (accountIds?: string[]): Promise<CallResult<WorkflowOrder[]>> => {
  if (!accountIds?.length) {
    return { data: null, error: { message: 'Must provide account IDs to fetch orders' } };
  }
  return callExternalApi<WorkflowOrder[]>({
    url: `api/orders?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getOpportunityQuotes = async (opportunitySfId: string): Promise<CallResult<Quote[]>> => {
  return callExternalApi<Quote[]>({
    url: `api/orders/${opportunitySfId}/quotes`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const postQuoteApproval = async (
  opportunityId: string,
  quoteIds: string[],
  opportunityName?: string,
  accountId?: string | null
): Promise<CallResult<QuoteApprovalResponse>> => {
  return callExternalApi<QuoteApprovalResponse>({
    url: `api/orders/${opportunityId}/approve`,
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(accountId ? { AccountScope: accountId } : {})
    },
    data: { quote_ids: quoteIds, opportunity_name: opportunityName }
  });
};
